import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import config from '../config.json';

interface LanguageSelectionProps {
  sessionUUID: string | null;
  show: boolean;
  onClose: () => void;
  tableName: string;
  columns: any[];
  onGenerate: (framework: string, backendType?: string) => void; 
}

const LanguageSelection: React.FC<LanguageSelectionProps> = ({
  sessionUUID,
  onClose,
  onGenerate,
  show,
  tableName,
  columns
}) => {

  const navigate = useNavigate();

  const [language, setLanguage] = useState<string | null>(null);
  const [framework, setFramework] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success',
  });

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  // PASO 1 → guarda lenguaje, limpia framework
  const handleLanguageChange = (selectedLanguage: string) => {
    setLanguage(selectedLanguage);
    setFramework(null);
  };

  // PASO 2 → genera backend + frontend, muestra modal de confirmación
  const handleFrameworkSelection = async (selectedFramework: string) => {
    setFramework(selectedFramework);
    setIsGenerating(true);

    if (language === 'Java') {
      // Java: genera Spring (backend) → luego el frontend elegido
      await onGenerate('Spring', 'spring');
      await onGenerate(selectedFramework, 'spring');
    } else if (language === 'Python') {
      // Python: genera FastAPI (backend) → luego el frontend elegido
      await onGenerate('Python',  'python');
      await onGenerate(selectedFramework,  'python');
    } else if (language === 'Node') {
      // Node: genera Express (backend) → luego el frontend elegido
      await onGenerate('Node', 'node');
      await onGenerate(selectedFramework, 'node');
    } else {
      await onGenerate(selectedFramework);
    }

    setIsGenerating(false);
    setShowSaveModal(true);
  };

  // PASO 3 → guarda en localStorage y navega a /preview
  const handleSave = () => {
    if (!sessionUUID || !language) {
      showToast('No se pudo guardar la configuración.', 'error');
      return;
    }
       // Leer tablas guardadas en localStorage
    const relationsKey = `omnicrud_relations_${sessionUUID}`;
    const savedRelations = JSON.parse(localStorage.getItem(relationsKey) || '{}');
    const tableNames = Object.keys(savedRelations);

    // ← backendType guardado en projectConfig
    const backendType = language === 'Python' ? 'python' : language === 'Node' ? 'node' : 'spring';

    localStorage.setItem('projectConfig', JSON.stringify({
      sessionUUID,
      language,
      architecture: 'MVC',
      framework,
      backendType,
      tables: tableNames,
    }));

    showToast(`Configuración guardada: ${language} - MVC${framework ? ' - ' + framework : ''}`);
    setShowSaveModal(false);
    navigate('/preview');
  };

  const handleCancel = () => {
    handleCloseAll();
  };

  const handleCloseAll = () => {
    setLanguage(null);
    setFramework(null);
    setShowSaveModal(false);
    setGenerationResult(null);
    setIsGenerating(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div>

      {/* ── MODAL PRINCIPAL ── */}
      {!showSaveModal && !isGenerating && (
        <div className="modal-overlay">
          <div className="modal-language-architecture">
            <h2>Configura tu proyecto</h2>

            {/* PASO 1 — elegir lenguaje/backend */}
            {!language && (
              <div className="language-selection">
                <button onClick={() => handleLanguageChange('Java')}>
                  <img src="/assets/java.svg" alt="Java Logo" />Java
                </button>
                <button onClick={() => handleLanguageChange('Python')}>
                  <img src="/assets/python.svg" alt="Python Logo" />Python
                </button>
                <button onClick={() => handleLanguageChange('Node')}>
                  <img src="/assets/nodejs.svg" alt="Node.js Logo" />Node.js
                </button>
                <button onClick={handleCloseAll} className="close">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            {/* PASO 2 — elegir frontend (igual para Java, Python y react) */}
            {(language === 'Java' || language === 'Python' || language === 'Node') && !framework && (
              <div className="framework-selection">
                <h3>Selecciona el framework frontend para {language === 'Java' ? 'Spring Boot' : language === 'Python' ? 'FastAPI' : 'Node.js Express'} + MVC</h3>
                <button onClick={() => handleFrameworkSelection('Angular')}>
                  <img src="/assets/angular.svg" alt="Angular Logo" />Angular
                </button>
                <button onClick={() => handleFrameworkSelection('Vue')}>
                  <img src="/assets/vue.svg" alt="Vue Logo" />Vue
                </button>
                <button onClick={() => handleFrameworkSelection('React')}>
                  <img src="/assets/react.svg" alt="React Logo" />React
                </button>
                <button onClick={() => setLanguage(null)} className="btn btn-secondary back-button">
                  <i className="fas fa-arrow-left"></i> Regresar
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── SPINNER GENERANDO ── */}
      {isGenerating && (
        <div className="modal-overlay">
          <div className="modal-language-architecture">
            <div className="text-center p-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Generando...</span>
              </div>
              <h3>Generando proyecto {framework || language}...</h3>
              <p>Por favor espera un momento</p>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL GUARDAR ── */}
      {showSaveModal && (
        <>
          <div className="modal-backdrop fade show custom-backdrop"></div>
          <div className="modal fade show" style={{ display: 'block' }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Guardar Configuración</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={handleCancel}></button>
                </div>
                <div className="modal-body">
                  <p>
                    ¿Estás seguro de que deseas guardar esta configuración?
                    <br />
                    <strong>Lenguaje:</strong> {language}<br />
                    <strong>Arquitectura:</strong> MVC<br />
                    {language === 'Java' && framework && (
                      <>
                        <strong>Backend:</strong> Spring Boot<br />
                        <strong>Frontend:</strong> {framework}
                      </>
                    )}
                    {language === 'Python' && framework && (
                      <>
                        <strong>Backend:</strong> FastAPI<br />
                        <strong>Frontend:</strong> {framework}
                      </>
                    )}
                    {language === 'Node' && framework && (
                      <>
                        <strong>Backend:</strong> Node.js Express<br />
                        <strong>Frontend:</strong> {framework}
                      </>
                    )}
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancelar</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave}>Aceptar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── TOAST ── */}
      {toast.show && (
      <div className="toast-container position-fixed end-0 p-3" style={{ zIndex: 9999, top: '70px' }}>
            <div className={`toast show text-bg-${toast.type === 'success' ? 'success' : 'danger'}`}>
            <div className="toast-header">
              <strong className="me-auto">{toast.type === 'success' ? 'Correcto' : 'Error'}</strong>
              <button
                type="button"
                className="btn-close"
                onClick={() => setToast(prev => ({ ...prev, show: false }))}
              />
            </div>
            <div className="toast-body">{toast.message}</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LanguageSelection;